import { useToast } from "@/hooks/use-toast";
import { useQueryClient, useMutation, useQuery } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { DollarSign, Loader2, TrendingUp } from "lucide-react";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { HostAccrual, HostCredit } from "./types";
import { formatCentsAdmin, formatDate } from "./helpers";

export function ReferHostAccrualsPanel() {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const query = useQuery<HostAccrual[]>({
    queryKey: ["/api/admin/refer-host/accruals"],
    queryFn: async () => {
      const res = await fetch("/api/admin/refer-host/accruals", { credentials: "include" });
      if (!res.ok) throw new Error("Failed");
      return res.json();
    },
  });

  const creditsQuery = useQuery<HostCredit[]>({
    queryKey: ["/api/admin/refer-host/credits"],
    queryFn: async () => {
      const res = await fetch("/api/admin/refer-host/credits", { credentials: "include" });
      if (!res.ok) throw new Error("Failed");
      return res.json();
    },
  });

  const voidAccrualMutation = useMutation({
    mutationFn: async (id: number) => {
      const res = await fetch(`/api/admin/refer-host/accruals/${id}`, {
        method: "PATCH",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ commissionCents: 0 }),
      });
      if (!res.ok) throw new Error("Failed to void accrual");
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/admin/refer-host/accruals"] });
      toast({ title: "Accrual voided (set to $0)" });
    },
    onError: () => toast({ title: "Failed to void accrual", variant: "destructive" }),
  });

  const voidCreditMutation = useMutation({
    mutationFn: async (id: number) => {
      const res = await fetch(`/api/admin/refer-host/credits/${id}`, {
        method: "PATCH",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ amountCents: 0, redeemedAt: new Date().toISOString() }),
      });
      if (!res.ok) throw new Error("Failed to void credit");
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/admin/refer-host/credits"] });
      toast({ title: "Credit voided" });
    },
    onError: () => toast({ title: "Failed to void credit", variant: "destructive" }),
  });

  const accruals = query.data || [];
  const credits = creditsQuery.data || [];

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <TrendingUp className="h-5 w-5 text-green-600" />
            Accruals Log
          </CardTitle>
        </CardHeader>
        <CardContent>
          {query.isLoading ? (
            <div className="flex items-center gap-2 text-muted-foreground py-8 justify-center">
              <Loader2 className="h-4 w-4 animate-spin" />
              Loading...
            </div>
          ) : accruals.length === 0 ? (
            <p className="text-muted-foreground text-center py-8">No accruals yet.</p>
          ) : (
            <Table data-testid="table-refer-host-accruals">
              <TableHeader>
                <TableRow>
                  <TableHead>Month</TableHead>
                  <TableHead>Referrer</TableHead>
                  <TableHead>Referred</TableHead>
                  <TableHead>Commission</TableHead>
                  <TableHead>Statement</TableHead>
                  <TableHead>Created</TableHead>
                  <TableHead></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {accruals.map((a) => (
                  <TableRow key={a.id} data-testid={`row-accrual-${a.id}`}>
                    <TableCell>{a.periodMonth}</TableCell>
                    <TableCell className="font-medium">{a.referrerEmail}</TableCell>
                    <TableCell>{a.referredEmail}</TableCell>
                    <TableCell className={`font-medium ${a.commissionCents === 0 ? "text-muted-foreground line-through" : "text-green-700"}`}>
                      {formatCentsAdmin(a.commissionCents)}
                    </TableCell>
                    <TableCell className="text-xs text-muted-foreground">{a.statementId ? `#${a.statementId}` : "Pending"}</TableCell>
                    <TableCell className="text-xs text-muted-foreground">{formatDate(a.createdAt)}</TableCell>
                    <TableCell>
                      {a.commissionCents > 0 && !a.statementId && (
                        <Button
                          size="sm"
                          variant="outline"
                          className="text-red-600 hover:text-red-700 h-7 px-2 text-xs"
                          onClick={() => voidAccrualMutation.mutate(a.id)}
                          disabled={voidAccrualMutation.isPending}
                          data-testid={`button-void-accrual-${a.id}`}
                        >
                          Void
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <DollarSign className="h-5 w-5 text-green-600" />
            Bill Credits Log
          </CardTitle>
        </CardHeader>
        <CardContent>
          {creditsQuery.isLoading ? (
            <div className="flex items-center gap-2 text-muted-foreground py-8 justify-center">
              <Loader2 className="h-4 w-4 animate-spin" />
              Loading...
            </div>
          ) : credits.length === 0 ? (
            <p className="text-muted-foreground text-center py-8">No credits issued yet.</p>
          ) : (
            <Table data-testid="table-refer-host-credits">
              <TableHeader>
                <TableRow>
                  <TableHead>Recipient User</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead>Amount</TableHead>
                  <TableHead>Expires</TableHead>
                  <TableHead>Redeemed</TableHead>
                  <TableHead></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {credits.map((c) => (
                  <TableRow key={c.id} data-testid={`row-credit-${c.id}`}>
                    <TableCell className="text-xs text-muted-foreground">user#{c.recipientUserId}</TableCell>
                    <TableCell className="text-xs capitalize">{c.creditType.replace(/_/g, " ")}</TableCell>
                    <TableCell className={`font-medium ${c.amountCents === 0 ? "text-muted-foreground line-through" : "text-green-700"}`}>
                      {formatCentsAdmin(c.amountCents)}
                    </TableCell>
                    <TableCell className="text-xs text-muted-foreground">{formatDate(c.expiresAt)}</TableCell>
                    <TableCell className="text-xs text-muted-foreground">{c.redeemedAt ? formatDate(c.redeemedAt) : "Active"}</TableCell>
                    <TableCell>
                      {!c.redeemedAt && c.amountCents > 0 && (
                        <Button
                          size="sm"
                          variant="outline"
                          className="text-red-600 hover:text-red-700 h-7 px-2 text-xs"
                          onClick={() => voidCreditMutation.mutate(c.id)}
                          disabled={voidCreditMutation.isPending}
                          data-testid={`button-void-credit-${c.id}`}
                        >
                          Void
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
